import { useState } from "react";
import { Loader2 } from "lucide-react";

import { VideoProvider } from "@/constants";
import type { Video } from "@/model/videos";
import {
  getVideoProvider,
  getVimeoEmbedUrl,
  getYoutubeEmbedUrl,
  isSafeVideoUrl,
} from "@/lib/video-embed";

// @types/react 18 does not know the `credentialless` iframe attribute yet
declare module "react" {
  interface IframeHTMLAttributes<T> extends HTMLAttributes<T> {
    credentialless?: "";
  }
}

// Links we can't turn into a YouTube / Vimeo player are framed with restrictions
const UNTRUSTED_FRAME_SANDBOX =
  "allow-scripts allow-same-origin allow-presentation allow-popups";

const getEmbedUrl = (provider: VideoProvider | null, link: string) => {
  if (provider === VideoProvider.YOUTUBE) return getYoutubeEmbedUrl(link);
  if (provider === VideoProvider.VIMEO) return getVimeoEmbedUrl(link);
  return null;
};

/**
 * Plays a video inside a 16:9 parent (`relative aspect-video`):
 * YouTube / Vimeo in their embed player, AWS files in a native <video>.
 */
const VideoMedia = ({ video }: { video: Video }) => {
  const [isFrameLoaded, setIsFrameLoaded] = useState(false);
  const provider = getVideoProvider(video.source);

  if (!isSafeVideoUrl(video.link)) {
    return (
      <div className="absolute inset-0 flex items-center justify-center text-white">
        Video not available
      </div>
    );
  }

  if (provider === VideoProvider.AWS) {
    return (
      <video
        src={video.link}
        poster={video.thumbNail}
        controls
        autoPlay
        playsInline
        className="absolute inset-0 size-full bg-black"
      />
    );
  }

  const embedUrl = getEmbedUrl(provider, video.link);

  return (
    <>
      {!isFrameLoaded && (
        <div className="absolute inset-0 flex items-center justify-center text-white">
          <Loader2 className="size-8 animate-spin" />
        </div>
      )}
      <iframe
        src={embedUrl ?? video.link}
        title={video.topic}
        onLoad={() => setIsFrameLoaded(true)}
        className="absolute inset-0 size-full border-0"
        allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
        allowFullScreen
        // YouTube (Error 153) and domain-restricted Vimeo videos need the referrer
        referrerPolicy="strict-origin-when-cross-origin"
        credentialless=""
        sandbox={embedUrl ? undefined : UNTRUSTED_FRAME_SANDBOX}
      />
    </>
  );
};

export default VideoMedia;
